import {
  BankOutlined,
  MenuOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import CajaStatusIndicator from "./components/Layout/CajaStatusIndicator";
import SyncStatusIndicator from "./components/Layout/SyncStatusIndicator";
import EnvironmentInfoIndicator from "./components/Layout/EnvironmentInfoIndicator";
import CriticalErrorModal from "./components/Layout/CriticalErrorModal";
import {
  ConfigProvider,
  Layout,
  Menu,
  Button,
  Tag,
  Tooltip,
  Dropdown,
  Modal,
  Spin,
} from "antd";
import esES from "antd/lib/locale/es_ES";
import { useEffect, useState, FC, JSX, ReactNode, useMemo } from "react";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import "./App.css";
import AperturaDeCajaManagement from "./components/AperturaDeCaja/AperturaDeCajaManagement";
import PrivateRoute from "./components/Auth/PrivateRoute";
import CajaManagement from "./components/Caja/CajaManagement";
import CierreDeCajaManagement from "./components/CierreDeCaja/CierreDeCajaManagement";
import ClientManagement from "./components/Client/ClientManagement";
import VendorManagement from "./components/Vendor";
import ColegioManagement from "./components/Colegio/ColegioManagement";
import CouponsManagement from "./components/Coupons/CouponsManagement";
import CampaignsManagement from "./components/Campaigns/CampaignsManagement";
import Dashboard from "./components/Dashboard/Dashboard";
import FormaDePagoManagement from "./components/FormaDePago/FormaDePagoManagement";
import InvoiceManagement from "./components/Invoice/InvoiceManagement";
import UserMenu from "./components/Layout/UserMenu";
import ProductManagement from "./components/Product/ProductManagement";
import SalesPriceManagement from "./components/SalesPrice/SalesPriceManagement";
import { StockQuery } from "./components/Stock";
import TPVManagement from "./components/TPV/TPVManagement";
import TurnoManagement from "./components/Turno/TurnoManagement";
import UserManagement from "./components/User/UserManagement";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { AppConfigProvider, useConfig } from "./contexts/ConfigContext";
import LoginPage from "./pages/LoginPage";
import ProfilePage from "./pages/ProfilePage";
import ReleaseNotesPage from "./pages/ReleaseNotesPage";
import SettingsPage from "./pages/SettingsPage";
import { clearSync, getSyncConfigStorageKey, setSyncUserContext, setupSync } from "./services/syncService";
import { EXPIRATION_DATE } from "./constants";
import packageInfo from "../package.json";
import NotificationManager from "./components/NotificationManager";
import { getMenuItems } from "./config/menu";
import Reports from "./components/reports/Reports";

// Componente LoginPage modificado con verificación de licencia
const LicenseProtectedLoginPage = (props: any) => {
  const [showExpiredModal, setShowExpiredModal] = useState(false);

  useEffect(() => {
    if (isLicenseExpired()) {
      setShowExpiredModal(true);
    }
  }, []);

  const handleModalClose = () => {
    setShowExpiredModal(false);
  };

  return (
    <>
      <LoginPage {...props} />
      <ExpiredLicenseModal
        visible={showExpiredModal}
        onClose={handleModalClose}
      />
    </>
  );
};


export interface AppRoute {
  path: string;
  element: ReactNode,
  private?: boolean,
  adminOnly?: boolean,
}

export const routes: AppRoute[] = [
  // Public
  { path: "/login", element: <LicenseProtectedLoginPage /> },

  // Dashboard
  { path: "/", element: <Dashboard />, private: true },
  { path: "/maestros", element: <Dashboard />, private: true },
  { path: "/gestion", element: <Dashboard />, private: true },

  // Admin
  {
    path: "/usuarios",
    element: <UserManagement />,
    private: true,
    adminOnly: true,
  },
  {
    path: "/clientes",
    element: <ClientManagement />,
    private: true,
    adminOnly: true,
  },
  {
    path: "/productos",
    element: <ProductManagement />,
    private: true,
    adminOnly: true,
  },
  {
    path: "/formas-pago",
    element: <FormaDePagoManagement />,
    private: true,
    adminOnly: true,
  },
  {
    path: "/colegios",
    element: <ColegioManagement />,
    private: true,
    adminOnly: true,
  },
  { path: "/tpvs", element: <TPVManagement />, private: true, adminOnly: true },
  {
    path: "/precios-venta",
    element: <SalesPriceManagement />,
    private: true,
    adminOnly: true,
  },
  {
    path: "/cupones",
    element: <CouponsManagement />,
    private: true,
    adminOnly: true,
  },
  {
    path: "/campanas",
    element: <CampaignsManagement />,
    private: true,
    adminOnly: true,
  },
  {
    path: "/vendedores",
    element: <VendorManagement />,
    private: true,
    adminOnly: true,
  },

  // Authenticated
  {
    path: "/facturas",
    element: <InvoiceManagement documentType="Invoice" />,
    private: true,
  },
  {
    path: "/abonos",
    element: <InvoiceManagement documentType="Credit Memo" />,
    private: true,
  },
  {
    path: "/facturas-pendientes",
    element: <InvoiceManagement syncStatus="pending" />,
    private: true,
  },
  { path: "/turnos", element: <TurnoManagement />, private: true },
  {
    path: "/aperturas-caja",
    element: <AperturaDeCajaManagement />,
    private: true,
  },
  { path: "/cajas", element: <CajaManagement />, private: true },
  { path: "/cierres-caja", element: <CierreDeCajaManagement />, private: true },
  { path: "/perfil", element: <ProfilePage />, private: true },
  { path: "/configuracion", element: <SettingsPage />, private: true },
  { path: "/notas-version", element: <ReleaseNotesPage />, private: true },
  { path: "/consulta-stock", element: <StockQuery />, private: true },
  { path: "/informes", element: <Reports />, private: true },
];

const { Header, Content, Sider } = Layout;

// Componente para mostrar el estado de conexión
const ConnectionStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      (window as any).messageApi.success(
        "Conexión restablecida. Sincronizando datos...",
        3,
      );
    };

    const handleOffline = () => {
      setIsOnline(false);
      (window as any).messageApi.info("Sin conexión. Trabajando en modo offline", 3);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  if (!isOnline) {
    return (
      <div
        style={{
          background: "#ffffb8",
          padding: "5px 10px",
          textAlign: "center",
          fontSize: "12px",
          width: "100%",
          position: "fixed",
          top: 0,
          zIndex: 1000,
        }}
      >
        Modo offline - Los cambios se sincronizarán cuando recuperes la conexión
      </div>
    );
  }

  return null;
};

// Componente para mostrar el tiempo restante de licencia
const LicenseCountdown = () => {
  const { logout } = useAuth();
  const [timeRemaining, setTimeRemaining] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [isExpired, setIsExpired] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const calculateTimeRemaining = () => {
      const expirationDate = new Date(
        +EXPIRATION_DATE.split("/")[2],
        +EXPIRATION_DATE.split("/")[1] - 1,
        +EXPIRATION_DATE.split("/")[0],
        23,
        59,
        59,
      );
      const now = new Date();
      const difference = expirationDate.getTime() - now.getTime();

      if (difference <= 0) {
        setIsExpired(true);
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor(
          (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
        ),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000),
      };
    };

    setTimeRemaining(calculateTimeRemaining());

    const timer = setInterval(() => {
      setTimeRemaining(calculateTimeRemaining());
    }, 1000);

    return () => clearInterval(timer);
  }, [logout]);

  // Si la licencia ha expirado, mostrar mensaje pero no cerrar sesión
  useEffect(() => {
    if (isExpired) {
      (window as any).messageApi.warning(
        "Su licencia ha expirado. Por favor contacte con el administrador.",
        3,
      );
    }
  }, [isExpired]);

  const getTagColor = () => {
    if (timeRemaining.days > 30) return "success";
    if (timeRemaining.days > 7) return "warning";
    return "error";
  };

  return (
    <Tooltip title="Tiempo restante de licencia">
      <Tag
        color={getTagColor()}
        icon={<ClockCircleOutlined />}
        style={{
          fontSize: isMobile ? "11px" : "12px",
          padding: isMobile ? "2px 6px" : "0 8px",
          margin: 0,
          whiteSpace: "nowrap",
        }}
      >
        {isMobile
          ? `${timeRemaining.days}d`
          : `Licencia: ${timeRemaining.days}d ${timeRemaining.hours}h ${timeRemaining.minutes}m`}
      </Tag>
    </Tooltip>
  );
};

// Función para verificar si la licencia ha expirado
const isLicenseExpired = () => {
  const expirationDate = new Date(
    +EXPIRATION_DATE.split("/")[2],
    +EXPIRATION_DATE.split("/")[1] - 1,
    +EXPIRATION_DATE.split("/")[0],
    23,
    59,
    59,
  );
  return new Date() > expirationDate;
};

// Componente para el modal de licencia expirada
const ExpiredLicenseModal: FC<any> = ({ visible, onClose }) => {
  return (
    <Modal
      title="⚠️ Licencia Expirada"
      open={visible}
      onOk={onClose}
      onCancel={onClose}
      closable={false}
      maskClosable={false}
      keyboard={false}
      okText="Aceptar para continuar"
      cancelButtonProps={{ style: { display: "none" } }}
      centered
      width={400}
    >
      <div style={{ padding: "20px 0", textAlign: "center" }}>
        <p style={{ fontSize: "16px", marginBottom: "16px" }}>
          Su licencia de ExceliaOne ha expirado el{" "}
          <strong>{EXPIRATION_DATE}</strong>.
        </p>
        <p style={{ fontSize: "14px", color: "#666" }}>
          Por favor contacte con el administrador para renovar su licencia.
        </p>
        <p style={{ fontSize: "14px", color: "#666", marginTop: "16px" }}>
          Pulse "Aceptar para continuar" para seguir usando la aplicación.
        </p>
      </div>
    </Modal>
  );
};

// Componente interno que usa useAuth
const AppContent = () => {
  const { isAuthenticated, loading: authLoading, currentUser } = useAuth() as any;
  const { country, loading: configLoading } = useConfig();
  const isUserAuthenticated = isAuthenticated();
  const shouldShowOverlay = authLoading || (isUserAuthenticated && (configLoading || !country));

  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showExpiredModal, setShowExpiredModal] = useState(false);

  const handleExpiredModalClose = () => {
    setShowExpiredModal(false);
  };

  // Función para manejar el cierre del menú al hacer clic fuera
  const handleOutsideClick = () => {
    if (isMobile && !collapsed) {
      setCollapsed(true);
    }
  };

  const menuItems = useMemo(() => getMenuItems({
    isAdmin: currentUser?.Admin || false,
    country,
  }), [currentUser, country]);

  // Verificar licencia expirada al cargar la aplicación
  useEffect(() => {
    if (isLicenseExpired() && isAuthenticated()) {
      setShowExpiredModal(true);
    }
  }, [isAuthenticated]);

  // Detectar recarga de página (F5) y mostrar modal si la licencia está expirada
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Marcar que la página se está recargando
      sessionStorage.setItem("pageReloading", "true");
    };

    const handleLoad = () => {
      // Si la página se recargó y la licencia está expirada, mostrar modal
      if (
        sessionStorage.getItem("pageReloading") === "true" &&
        isLicenseExpired() &&
        isAuthenticated()
      ) {
        setShowExpiredModal(true);
        sessionStorage.removeItem("pageReloading");
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("load", handleLoad);

    // Verificar al montar el componente si es una recarga
    if (isLicenseExpired() && isAuthenticated()) {
      setShowExpiredModal(true);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("load", handleLoad);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth < 768) {
        setCollapsed(true);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!currentUser) {
      clearSync();
      setSyncUserContext(null);
    }
    else {
      setSyncUserContext(currentUser);
    }

    let cancelled = false;
    (async () => {
      try {
        const config = localStorage.getItem(getSyncConfigStorageKey());
        await setupSync(config ? JSON.parse(config) : null, { isUserAuthenticated });
      } catch (error) {
        console.error("Error al inicializar servicio de sincronización:", error);
        if (!cancelled) {
          (window as any).messageApi.warning(
            "El servicio de sincronización no pudo inicializarse correctamente. Algunas funciones pueden no estar disponibles.",
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authLoading, currentUser?.id]);

  // Si el usuario no está autenticado, mostrar solo la página de login
  if (!isUserAuthenticated) {
    return (
      <Routes>
        <Route path="*" element={<LicenseProtectedLoginPage />} />
      </Routes>
    );
  }

  return (
    <Spin spinning={shouldShowOverlay} tip="Cargando configuración del país..." size="large" style={{ maxHeight: '100vh' }}>
      <Layout style={{ minHeight: "100vh" }}>
        <ConnectionStatus />

        {/* Overlay para cerrar el menú al hacer clic fuera en modo móvil */}
        {isMobile && !collapsed && isUserAuthenticated && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.45)",
              zIndex: 998,
            }}
            onClick={handleOutsideClick}
          />
        )}

        {isUserAuthenticated && (
          <Sider
            collapsible
            collapsed={collapsed}
            onCollapse={(value) => setCollapsed(value)}
            width={200}
            style={{
              overflow: "auto",
              height: "100vh",
              position: "fixed",
              left: 0,
              top: 0,
              bottom: 0,
              zIndex: 999,
            }}
            theme="dark"
            breakpoint="lg"
            collapsedWidth={isMobile ? 0 : 80}
            {...(isMobile ? { trigger: null } : {})}
          >
            <div
              className="logo"
              style={{
                height: "32px",
                margin: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: !collapsed ? "flex-start" : "center",
                color: "white",
                fontSize: !collapsed ? "18px" : "0px",
                overflow: "hidden",
              }}
            >
              {!collapsed && "ExceliaOne"}
              {collapsed && <MenuOutlined />}
            </div>
            <Menu
              theme="dark"
              mode="inline"
              defaultSelectedKeys={["1"]}
              items={menuItems as any}
              style={{
                border: "none",
                height: "calc(100vh - 120px)", // Dejar espacio para logo (64px) y versión (56px)
                overflow: "auto",
              }}
            />
            {/* Mostrar versión en la parte inferior del menú */}
            <div
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                padding: "12px 16px",
                borderTop: "1px solid #434343",
                background: "#001529",
                color: "#8c8c8c",
                fontSize: "11px",
                textAlign: "center",
                height: "50px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
              }}
            >
              {!collapsed && (
                <div>
                  <div
                    style={{
                      fontWeight: "500",
                      fontSize: "12px",
                      color: "white",
                    }}
                  >
                    {country?.name || ""}
                  </div>
                  <div
                    style={{ marginTop: "2px", opacity: 0.8, fontSize: "10px" }}
                  >
                    v{packageInfo.version}
                  </div>
                </div>
              )}
              {collapsed && (
                <div
                  style={{
                    fontSize: "10px",
                    color: "white",
                    transform: "rotate(-90deg)",
                  }}
                >
                  v{packageInfo.version}
                </div>
              )}
            </div>
          </Sider>
        )}

        <Layout
          style={{
            marginLeft:
              isUserAuthenticated && !isMobile && !collapsed
                ? 200
                : isUserAuthenticated && !isMobile
                  ? 80
                  : 0,
          }}
        >
          {isUserAuthenticated && (
            <Header
              style={{
                padding: "0 8px",
                background: "#001529",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                boxShadow: "0 1px 4px rgba(0, 0, 0, 0.2)",
                position: "sticky",
                top: 0,
                zIndex: 998,
                width: "100%",
                color: "white",
                height: isMobile ? "48px" : "64px",
                minHeight: isMobile ? "48px" : "64px",
              }}
            >
              {/* Botón para controlar el menú lateral en móvil */}
              <div style={{ display: "flex", alignItems: "center" }}>
                {isMobile && (
                  <Button
                    type="text"
                    icon={<MenuOutlined />}
                    onClick={() => setCollapsed(!collapsed)}
                    style={{
                      fontSize: "16px",
                      width: 32,
                      height: 32,
                      color: "white",
                      padding: 0,
                    }}
                  />
                )}
                {!isMobile && <div style={{ width: 32 }}></div>}

                {/* Solo mostrar este título en escritorio o hacer que sea mucho más pequeño en móvil */}
                {isMobile && (
                  <span style={{ marginLeft: 16, fontSize: 16 }}>ExceliaOne</span>
                )}
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  justifyContent: "flex-end",
                }}
              >
                {isMobile ? (
                  <Dropdown
                    dropdownRender={() => (
                      <div
                        style={{
                          background: "#fff",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                          borderRadius: "4px",
                          padding: "8px",
                          marginTop: "8px",
                          color: "black",
                        }}
                      >
                        <CajaStatusIndicator />
                        <div style={{ marginTop: "8px" }}>
                          <LicenseCountdown />
                        </div>
                      </div>
                    )}
                    trigger={["click"]}
                    placement="bottomRight"
                  >
                    <Button
                      type="text"
                      icon={<BankOutlined />}
                      style={{
                        color: "white",
                        padding: 0,
                        width: 32,
                        height: 32,
                      }}
                    />
                  </Dropdown>
                ) : (
                  <>
                    <CajaStatusIndicator />
                    <LicenseCountdown />
                  </>
                )}
                <UserMenu />
              </div>
            </Header>
          )}

          <Content
            style={{
              margin: isMobile ? "8px" : "16px",
              padding: isMobile ? "12px" : "24px",
              background: "#fff",
              borderRadius: "8px",
              minHeight: 280,
              boxShadow: "0 1px 4px rgba(0, 0, 0, 0.05)",
            }}
          >
            <Routes>
              {routes.map(({ path, element, private: isPrivate, adminOnly }) => {
                if (adminOnly && !currentUser?.Admin) return null;

                const wrappedElement = isPrivate ? (
                  <PrivateRoute>{element}</PrivateRoute>
                ) : (
                  element
                );

                return <Route key={path} path={path} element={wrappedElement} />;
              })}

              {/* Fallback */}
              <Route
                path="*"
                element={
                  <div
                    style={{ textAlign: "center", padding: 50, color: "#ff4d4f" }}
                  >
                    <h2>Acceso No Autorizado</h2>
                    <p>No tienes permisos para acceder a esta sección.</p>
                    <p>Contacta con el administrador si necesitas acceso.</p>
                  </div>
                }
              />
            </Routes>
          </Content>
        </Layout>

        {/* Modal de licencia expirada */}
        <ExpiredLicenseModal
          visible={showExpiredModal}
          onClose={handleExpiredModalClose}
        />
      </Layout>
    </Spin>
  );
};

// Componente principal App que no usa useAuth directamente
function App() {
  return (
    <ConfigProvider
      locale={esES}
      theme={{
        token: {
          colorPrimary: "#1677ff",
          borderRadius: 6,
        },
      }}
    >
      <Router>
        <NotificationManager />
        <AuthProvider>
          <AppConfigProvider>
            <AppContent />
            {/* Indicadores y modal de error montados globalmente */}
            <SyncStatusIndicator />
            <EnvironmentInfoIndicator />
            <CriticalErrorModal />
          </AppConfigProvider>
        </AuthProvider>
      </Router>
    </ConfigProvider>
  );
}

export default App;